import { useMutation, useQueryClient } from '@tanstack/react-query'

import { authStorage, normalizeBearerToken } from '@/lib/auth-storage'
import { http } from '@/lib/http'
import { resetTracking, clearTracking } from '@/lib/action-chain'
import { useAuthStore } from '@/stores/auth.store'

import { fetchCurrentUser } from '../queries/auth'
import {
  AuthenticationTokenSchema,
  type LoginForm,
} from '../schemas/auth'
import { ResultSchema } from '../schemas/common'

export const useLoginMutation = () => {
  const setAuth = useAuthStore((state) => state.setAuth)
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (form: LoginForm) => {
      const json = await http.post('auth/frontend/login', { json: form }).json()
      const token = ResultSchema(AuthenticationTokenSchema).parse(json).data
      if (!token) throw new Error('Empty login response')
      authStorage.set(token.accessToken, token.refreshToken, token.expireTime)

      try {
        const user = await fetchCurrentUser()
        return { token, user }
      } catch (error) {
        authStorage.clear()
        throw error
      }
    },
    onSuccess: ({ token, user }) => {
      setAuth({
        user,
        accessToken: normalizeBearerToken(token.accessToken),
        refreshToken: normalizeBearerToken(token.refreshToken),
        expiresInSec: token.expireTime,
      })
      queryClient.setQueryData(['auth', 'me'], user)
      // 登录成功：开启一条新的行为链路
      resetTracking()
    },
  })
}

export const useLogoutMutation = () => {
  const logout = useAuthStore((state) => state.logout)

  return useMutation({
    mutationFn: async () => {
      await http.delete('auth/logout').catch(() => undefined)
    },
    onSettled: () => {
      clearTracking()
      logout()
    },
  })
}
